/**
 * fincode.js SDK ローダー
 *
 * fincode の JavaScript SDK を使用して、
 * カードトークン化機能を提供します。
 */

import { initFincode } from '@fincode/js';

/**
 * fincode のカードトークンを生成
 *
 * @param cardData カード情報
 * @returns トークン
 */
export interface CardData {
  cardNumber: string;
  expire: string; // YYMM 形式
  securityCode: string;
  holderName?: string;
}

export const createCardToken = async (cardData: CardData, testMode: boolean = true): Promise<string> => {
  const publicKey = testMode
    ? import.meta.env.VITE_FINCODE_PUBLIC_KEY_TEST
    : import.meta.env.VITE_FINCODE_PUBLIC_KEY;

  if (!publicKey) {
    throw new Error(`fincode public key not configured (${testMode ? 'test' : 'live'} mode)`);
  }

  const isLiveMode = !testMode;

  const fincode = await initFincode({
    publicKey,
    isLiveMode,
  });

  return new Promise((resolve, reject) => {
    fincode.tokens(
      {
        card_no: cardData.cardNumber.replace(/\s/g, ''),
        expire: cardData.expire,
        security_code: cardData.securityCode,
        holder_name: cardData.holderName || '',
      },
      (status: number, response: any) => {
        if (status === 200 && response.list && response.list.length > 0) {
          resolve(response.list[0].token);
        } else {
          reject(new Error('トークン生成に失敗しました'));
        }
      },
      () => {
        reject(new Error('トークン生成中にエラーが発生しました'));
      }
    );
  });
};

/**
 * カード番号のバリデーション
 */
export const validateCardNumber = (cardNumber: string): boolean => {
  const cleaned = cardNumber.replace(/\s/g, '');

  // 長さチェック（13-19桁）
  if (!/^\d{13,19}$/.test(cleaned)) {
    return false;
  }

  // Luhn アルゴリズム
  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * カード番号をフォーマット（4桁ごとにスペース）
 */
export const formatCardNumber = (value: string): string => {
  const cleaned = value.replace(/\s/g, '');
  const groups = cleaned.match(/.{1,4}/g) || [];
  return groups.join(' ');
};

/**
 * 有効期限のバリデーション（MM/YY 形式）
 */
export const validateExpiry = (expiry: string): boolean => {
  const match = expiry.match(/^(\d{2})\/(\d{2})$/);
  if (!match) return false;

  const month = parseInt(match[1], 10);
  const year = parseInt('20' + match[2], 10);

  if (month < 1 || month > 12) return false;

  const now = new Date();
  const expiryDate = new Date(year, month - 1, 1);

  return expiryDate > now;
};

/**
 * 有効期限をフォーマット（MM/YY 形式）
 */
export const formatExpiry = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length >= 2) {
    return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
  }
  return cleaned;
};

/**
 * 有効期限を YYMM 形式に変換（fincode API 用）
 */
export const expiryToYYMM = (expiry: string): string => {
  const match = expiry.match(/^(\d{2})\/(\d{2})$/);
  if (!match) throw new Error('Invalid expiry format');

  return match[2] + match[1]; // YYMM
};

/**
 * セキュリティコードのバリデーション
 */
export const validateSecurityCode = (code: string): boolean => {
  return /^\d{3,4}$/.test(code);
};

/**
 * カードブランドを判定
 */
export const getCardBrand = (cardNumber: string): string | null => {
  const cleaned = cardNumber.replace(/\s/g, '');

  if (/^4/.test(cleaned)) return 'Visa';
  if (/^5[1-5]/.test(cleaned)) return 'MasterCard';
  if (/^3[47]/.test(cleaned)) return 'American Express';
  if (/^35/.test(cleaned)) return 'JCB';
  if (/^6(?:011|5)/.test(cleaned)) return 'Discover';

  return null;
};
